import React from 'react'
import { Form, Input, Select } from 'antd'
import { merchantsService } from '../../../../shared/api/services/merchants.service'
import { DocumentUpload } from '../../../../components/DocumentUpload'
import {
    BusinessType,
    BusinessConstitution,
    BUSINESS_TYPE_LABELS,
    BUSINESS_CONSTITUTION_LABELS,
    isCommissionBasedType,
} from '../../../../shared/constants/businessEnums'

const { Option } = Select

export const BusinessDetailsStep: React.FC = () => {
    const form = Form.useFormInstance();
    const businessType = Form.useWatch('business_type', form);

    const isGstRequired = businessType ? !isCommissionBasedType(businessType) : false;

    const isEntranceImageRequired = businessType ? !isCommissionBasedType(businessType) : true;

    const validateAvailability = async (value: string, type: string) => {
        if (!value) return Promise.resolve();
        // Only hit the API when the value is complete, to avoid calls while typing
        const isComplete = type === 'pan_number'
            ? value.length === 10
            : value.length === 15;
        if (!isComplete) return Promise.resolve();
        try {
            const isAvailable = await merchantsService.checkAvailability(type, value);
            if (!isAvailable) {
                return Promise.reject(new Error(`This ${type.replace('_', ' ').toUpperCase()} is already registered`));
            }
            return Promise.resolve();
        } catch (error) {
            console.error('Failed to validate availability:', error);
            return Promise.resolve(); // Fail validation open on API error or let it pass
        }
    }

    return (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <Form.Item
                label="Business Name"
                name="business_name"
                rules={[{ required: true, message: 'Please enter business name' }]}
            >
                <Input placeholder="Enter business name" />
            </Form.Item>

            <Form.Item
                label="Business Type"
                name="business_type"
                rules={[{ required: true, message: 'Please select business type' }]}
            >
                <Select placeholder="Select Business Type">
                    <Option value={BusinessType.DISTRIBUTOR}>{BUSINESS_TYPE_LABELS[BusinessType.DISTRIBUTOR]}</Option>
                    <Option value={BusinessType.MANUFACTURER}>{BUSINESS_TYPE_LABELS[BusinessType.MANUFACTURER]}</Option>
                    <Option value={BusinessType.WHOLESALER}>{BUSINESS_TYPE_LABELS[BusinessType.WHOLESALER]}</Option>
                    <Option value={BusinessType.COMMISSION_AGENT}>{BUSINESS_TYPE_LABELS[BusinessType.COMMISSION_AGENT]}</Option>
                </Select>
            </Form.Item>

            <Form.Item
                label="Business Constitution"
                name="business_constitution"
                rules={[{ required: true, message: 'Please select constitution' }]}
            >
                <Select placeholder="Select Business Constitution">
                    <Option value={BusinessConstitution.PROPRIETORSHIP}>{BUSINESS_CONSTITUTION_LABELS[BusinessConstitution.PROPRIETORSHIP]}</Option>
                    <Option value={BusinessConstitution.PARTNERSHIP}>{BUSINESS_CONSTITUTION_LABELS[BusinessConstitution.PARTNERSHIP]}</Option>
                    <Option value={BusinessConstitution.LLP}>{BUSINESS_CONSTITUTION_LABELS[BusinessConstitution.LLP]}</Option>
                    <Option value={BusinessConstitution.PRIVATE_LIMITED}>{BUSINESS_CONSTITUTION_LABELS[BusinessConstitution.PRIVATE_LIMITED]}</Option>
                    <Option value={BusinessConstitution.PUBLIC_LIMITED}>{BUSINESS_CONSTITUTION_LABELS[BusinessConstitution.PUBLIC_LIMITED]}</Option>
                </Select>
            </Form.Item>

            <Form.Item
                label="PAN Number"
                name="pan_number"
                normalize={(value) => (value ? String(value).toUpperCase() : value)}
                rules={[
                    { required: true, message: 'Please enter PAN number' },
                    { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'Invalid PAN format' },
                    { validator: (_, value) => validateAvailability(value, 'pan_number') }
                ]}
                validateTrigger={['onBlur', 'onChange']}
            >
                <Input placeholder="ABCDE1234F" style={{ textTransform: 'uppercase' }} />
            </Form.Item>

            <Form.Item
                label="PAN Card Image"
                name="pan_document"
                rules={[{ required: true, message: 'Please upload PAN card image' }]}
            >
                <DocumentUpload documentType="pan_card" />
            </Form.Item>

            <Form.Item
                label="GST Number"
                name="gst_number"
                normalize={(value) => (value ? String(value).toUpperCase() : value)}
                rules={[
                    { required: isGstRequired, message: 'Please enter GST number' },
                    { pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Invalid GST format' },
                    { validator: (_, value) => validateAvailability(value, 'gst_number') }
                ]}
                validateTrigger={['onBlur', 'onChange']}
            >
                <Input placeholder="22AAAAA0000A1Z5" style={{ textTransform: 'uppercase' }} />
            </Form.Item>

            <Form.Item
                label="GST Certificate"
                name="gst_document"
                rules={[{ required: isGstRequired, message: 'Please upload GST certificate' }]}
            >
                <DocumentUpload documentType="gst_certificate" />
            </Form.Item>

            <Form.Item
                label="Store Entrance Image"
                name="entrance_image"
                rules={[{ required: isEntranceImageRequired, message: 'Please upload store entrance image' }]}
            >
                <DocumentUpload documentType="business_entrance" />
            </Form.Item>
        </div>
    )
}
